import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { DataService } from '../../services/data.service';
import { ApiService } from '../../services/api.service';
import { MenuActionObject } from '../../models/menu.model';
import { MenuAction, OrderStatus, PaymentMode } from '../../shared/enums';
import { WaiterComponent } from '../../shared/waiter/waiter.component';
import { PayAtTablePaymentService } from '../../services/pay-at-table-payment.service';
import { SHOW_MENU_HOME_STATE } from '../menu-home.guard';

interface BottomBarAction {
  source: MenuActionObject;
  icon: string;
  showBillBadge: boolean;
}

@Component({
  selector: 'app-bottom-bar',
  standalone: false,
  templateUrl: './bottom-bar.component.html',
  styleUrl: './bottom-bar.component.scss',
})
export class BottomBarComponent implements OnInit {
  private dataService = inject(DataService);
  private apiService = inject(ApiService);
  private payAtTablePaymentService = inject(PayAtTablePaymentService);
  private router = inject(Router);
  private bottomSheet = inject(MatBottomSheet);

  private restaurant = computed(() => this.dataService.restaurant());
  private hasUnpaidItems = signal(false);

  actions = computed<BottomBarAction[]>(() => {
    const configured = this.dataService.menuActions();
    const ezetapMachineId = this.dataService.ezetapMachineId();
    const sources = ezetapMachineId && configured.length > 0
      ? configured.filter((action) => action.action !== MenuAction.Orders && action.action !== MenuAction.Bill)
      : configured;

    const unpaid = this.hasUnpaidItems();
    return sources.map((source) => ({
      source,
      icon: this.iconFor(source),
      showBillBadge: source.action === MenuAction.Bill && unpaid,
    }));
  });

  public ngOnInit(): void {
    this.loadUnpaidItems();
  }

  public onMenuClick(): void {
    this.router.navigate(['/menu-home'], { state: SHOW_MENU_HOME_STATE });
  }

  public onActionClick(action: MenuActionObject): void {
    switch (action.action) {
      case MenuAction.Waiter:
        this.bottomSheet.open(WaiterComponent);
        break;
      case MenuAction.Bill:
      case MenuAction.Orders:
        if (this.restaurant()?.payment_mode === PaymentMode.PayAtTable) {
          this.payAtTablePaymentService.setOrigin(this.router.url);
          this.router.navigate(['/pay-at-table']);
        } else {
          this.router.navigate(['/bill']);
        }
        break;
      case MenuAction.Whatsapp:
        this.openWhatsapp();
        break;
      case MenuAction.Directory: {
        const url = this.restaurant()?.directory_url;
        if (url) window.open(url);
        break;
      }
      case MenuAction.Custom:
        window.open(action.config.url || '_self');
        break;
    }
  }

  private iconFor(action: MenuActionObject): string {
    if (action.action === MenuAction.Custom) return `${action.config.type}.svg`;
    return `${action.action}.svg`;
  }

  private openWhatsapp(): void {
    const phoneNumber = this.restaurant()?.phone_number;
    if (!phoneNumber) return;

    const cleanNumber = phoneNumber.replace('+', '').replace(' ', '');
    const table = this.dataService.table();
    const message = `Hello, this is ${table.table_type} ${table.name}`;
    window.open(encodeURI(`https://wa.me/${cleanNumber}?text=${message}`));
  }

  private loadUnpaidItems(): void {
    const userId = this.dataService.getUserId();
    const restaurantId = this.restaurant()?.id;
    if (!userId || !restaurantId) {
      return;
    }

    this.apiService.getOrders(userId, restaurantId).subscribe({
      next: (res) => {
        this.hasUnpaidItems.set(
          (res.orders || []).some((order) => (order.status || '').toLowerCase() === OrderStatus.Unpaid)
        );
      },
      error: () => {
        this.hasUnpaidItems.set(false);
      },
    });
  }
}
